import { useState } from "react";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";
import { graphql, useFragment, useMutation } from "react-relay";
import { InferOutput, object } from "valibot";
import { logger } from "@/common/logger";
import { Edit03Icon } from "@/icons";
import { Button, ButtonProps } from "@/kit/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/kit/ui/form";
import { Input } from "@/kit/ui/input";
import {
  ResponsiveModal,
  ResponsiveModalClose,
  ResponsiveModalContent,
  ResponsiveModalDescription,
  ResponsiveModalPortal,
  ResponsiveModalTitle,
  ResponsiveModalTrigger,
} from "@/kit/ui/responsive-modal";
import { relayErrorMessage } from "@/utils/relay";
import { semverSchema } from "@/utils/schema";
import { toast } from "@/utils/toast";
import { useValibotForm } from "@/utils/validation";
import { WorkspaceVersionRenameButtonFragment$key } from "./__generated__/WorkspaceVersionRenameButtonFragment.graphql";
import { WorkspaceVersionRenameButtonMutation } from "./__generated__/WorkspaceVersionRenameButtonMutation.graphql";

const Fragment = graphql`
  fragment WorkspaceVersionRenameButtonFragment on WorkspaceVersion {
    id
    version
  }
`;

const Mutation = graphql`
  mutation WorkspaceVersionRenameButtonMutation(
    $id: ID!
    $input: UpdateWorkspaceVersionInput!
  ) {
    updateWorkspaceVersion(id: $id, input: $input) {
      id
      version
    }
  }
`;

const FormSchema = (intl: IntlShape) =>
  object({
    version: semverSchema(intl),
  });
type SubmitData = InferOutput<ReturnType<typeof FormSchema>>;

export interface WorkspaceVersionRenameButtonProps extends ButtonProps {
  version: WorkspaceVersionRenameButtonFragment$key;
}

export default function WorkspaceVersionRenameButton({
  version: versionFragment,
  ...props
}: WorkspaceVersionRenameButtonProps) {
  const intl = useIntl();
  const [isOpen, setOpen] = useState(false);
  const version = useFragment(Fragment, versionFragment);
  const [commitMutation, isMutationInFlight] =
    useMutation<WorkspaceVersionRenameButtonMutation>(Mutation);
  const form = useValibotForm(FormSchema(intl), {
    mode: "onChange",
    defaultValues: { version: version.version },
  });

  const onSubmit = (submitData: SubmitData) =>
    commitMutation({
      variables: { id: version.id, input: { version: submitData.version } },
      onCompleted: () => {
        toast.success(
          <FormattedMessage defaultMessage="Workspace version renamed" />,
        );
        setOpen(false);
      },
      onError: (error) => {
        logger.error(error);
        toast.error(relayErrorMessage(error));
      },
    });

  const onOpenChange = (open: boolean) => {
    if (isMutationInFlight) return;
    setOpen(open);
  };

  return (
    <ResponsiveModal open={isOpen} onOpenChange={onOpenChange}>
      <ResponsiveModalTrigger asChild>
        <Button {...props} />
      </ResponsiveModalTrigger>

      <ResponsiveModalPortal>
        <ResponsiveModalContent
          className="sm:w-96 w-auto bg-linear-to-b from-[#EFEDFC] from-[-16.39%] to-white to-[65.62%]"
          noClose={isMutationInFlight}
        >
          <Form form={form} onSubmit={onSubmit} className="w-full">
            <div className="flex flex-col items-center justify-center gap-3">
              <div className="bg-brand-50 rounded-md p-2 w-fit">
                <Edit03Icon className="text-brand-500 w-8 h-8" />
              </div>
              <ResponsiveModalTitle>
                <FormattedMessage defaultMessage="Rename version" />
              </ResponsiveModalTitle>
              <ResponsiveModalDescription className="text-center w-10/12">
                <FormattedMessage
                  defaultMessage="Give “{version}” a new SemVer name. Only unpublished versions can be renamed."
                  values={{ version: version.version }}
                />
              </ResponsiveModalDescription>

              <FormField
                control={form.control}
                name="version"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>
                      <FormattedMessage defaultMessage="Version name" />
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={intl.formatMessage({
                          defaultMessage: "Enter a unique SemVer name",
                        })}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="w-full flex flex-row gap-2 mt-4">
                <ResponsiveModalClose asChild>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={isMutationInFlight}
                    className="w-full"
                  >
                    <FormattedMessage defaultMessage="Cancel" />
                  </Button>
                </ResponsiveModalClose>
                <Button
                  type="submit"
                  disabled={isMutationInFlight || !form.formState.isDirty}
                  className="w-full"
                >
                  <FormattedMessage defaultMessage="Save" />
                </Button>
              </div>
            </div>
          </Form>
        </ResponsiveModalContent>
      </ResponsiveModalPortal>
    </ResponsiveModal>
  );
}
